import { create } from "zustand";

// In-app feedback that replaces window.alert / window.confirm: toasts for
// outcomes ("Saved", "Couldn't save: …") and a styled confirmation dialog.
// Rendered once by <FeedbackLayer /> at the app root.

export type ToastTone = "ok" | "err" | "info";
export interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

export interface ConfirmRequest {
  title: string;
  body?: string;
  confirmLabel?: string;
  danger?: boolean;
  resolve: (ok: boolean) => void;
}

interface FeedbackState {
  toasts: Toast[];
  push: (tone: ToastTone, message: string) => void;
  dismiss: (id: number) => void;
  confirmRequest: ConfirmRequest | null;
  setConfirm: (r: ConfirmRequest | null) => void;
}

let nextId = 1;

export const useFeedback = create<FeedbackState>()((set) => ({
  toasts: [],
  push: (tone, message) => {
    const id = nextId++;
    set((s) => ({ toasts: [...s.toasts.slice(-3), { id, tone, message }] }));
    window.setTimeout(() => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })), 4500);
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  confirmRequest: null,
  setConfirm: (confirmRequest) => set({ confirmRequest }),
}));

export const toast = {
  ok: (message: string) => useFeedback.getState().push("ok", message),
  err: (message: string) => useFeedback.getState().push("err", message),
  info: (message: string) => useFeedback.getState().push("info", message),
};

/** Ask the user to confirm. Resolves true on confirm, false on cancel. */
export function confirmDialog(opts: Omit<ConfirmRequest, "resolve">): Promise<boolean> {
  return new Promise((resolve) => {
    useFeedback.getState().setConfirm({
      ...opts,
      resolve: (ok) => {
        useFeedback.getState().setConfirm(null);
        resolve(ok);
      },
    });
  });
}
